import { parseISO } from 'date-fns';

import type { StorageAdapter } from '@/services/storage/storage-adapter';
import type { LogEntry } from '@/types/log-entry';
import type { DateRange, PeriodSummary, ComparisonPeriod, ComparisonResult } from '@/types/comparison';

function computeSummary(entries: LogEntry[]): PeriodSummary {
  let totalObservations = 0;
  let buckCount = 0;
  let doeCount = 0;
  let fawnCount = 0;
  let tempSum = 0;
  let tempCount = 0;
  let pressureSum = 0;
  let pressureCount = 0;
  let photoCount = 0;
  const tagCounts = new Map<string, number>();
  const windCounts = new Map<string, number>();

  for (const entry of entries) {
    if (entry.animalObserved) {
      const count = entry.animalCount ?? 1;
      totalObservations += count;
      if (entry.animalSex === 'buck') buckCount += count;
      else if (entry.animalSex === 'doe') doeCount += count;
      else if (entry.animalSex === 'fawn') fawnCount += count;
    }

    for (const tag of entry.tags) {
      tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
    }

    if (entry.windDirection) {
      windCounts.set(entry.windDirection, (windCounts.get(entry.windDirection) ?? 0) + 1);
    }

    if (entry.weatherTemp !== undefined) {
      tempSum += entry.weatherTemp;
      tempCount++;
    }

    if (entry.barometricPressure !== undefined) {
      pressureSum += entry.barometricPressure;
      pressureCount++;
    }

    photoCount += entry.photos?.length ?? 0;
  }

  const topTags = [...tagCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([tag]) => tag);

  const commonWindDirections = [...windCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3)
    .map(([dir]) => dir);

  return {
    totalEntries: entries.length,
    totalObservations,
    buckCount,
    doeCount,
    fawnCount,
    avgTemp: tempCount > 0 ? Math.round((tempSum / tempCount) * 10) / 10 : null,
    avgPressure: pressureCount > 0 ? Math.round((pressureSum / pressureCount) * 100) / 100 : null,
    topTags,
    commonWindDirections,
    photoCount,
  };
}

function sortAscending(entries: LogEntry[]): LogEntry[] {
  return [...entries].sort((a, b) => a.date.localeCompare(b.date));
}

function buildPeriod(entries: LogEntry[], range: DateRange): ComparisonPeriod {
  const sorted = sortAscending(entries);
  return { range, entries: sorted, summary: computeSummary(sorted) };
}

export class ComparisonService {
  constructor(private adapter: StorageAdapter) {}

  async getComparisonData(
    waypointId: string,
    periodA: DateRange,
    periodB: DateRange,
  ): Promise<ComparisonResult> {
    const [rawA, rawB] = await Promise.all([
      this.adapter.getLogEntriesByDateRange(waypointId, parseISO(periodA.start), parseISO(periodA.end)),
      this.adapter.getLogEntriesByDateRange(waypointId, parseISO(periodB.start), parseISO(periodB.end)),
    ]);

    return {
      periodA: buildPeriod(rawA, periodA),
      periodB: buildPeriod(rawB, periodB),
    };
  }

  async getPropertyComparisonData(
    propertyId: string,
    periodA: DateRange,
    periodB: DateRange,
  ): Promise<ComparisonResult> {
    const allEntries = await this.adapter.getLogEntriesByProperty(propertyId);

    const filteredA = allEntries.filter(
      (e) => e.date >= periodA.start && e.date <= periodA.end,
    );
    const filteredB = allEntries.filter(
      (e) => e.date >= periodB.start && e.date <= periodB.end,
    );

    return {
      periodA: buildPeriod(filteredA, periodA),
      periodB: buildPeriod(filteredB, periodB),
    };
  }
}
